/**
 * Templates (spec §25-33). Templates are core product IP and are versioned
 * JSON, never executable code (spec §25): a template is data that the
 * deterministic renderer interprets, so it can be stored, diffed, snapshotted
 * and golden-tested. A template must never evaluate an arbitrary expression.
 *
 * Two safety properties are encoded here:
 *
 *  1. Bindings are a WHITELIST (spec §31). A text/logo/image layer draws its
 *     value from a fixed set of paths (`auction.name`, `lot.hipNumber`, ...),
 *     not from a string the template author typed. A template cannot bind to
 *     anything the domain has not blessed.
 *  2. Templates are IMMUTABLE BY VERSION (spec §33). A campaign records the
 *     template version it used; publishing v2 never rewrites assets made with
 *     v1. The `TemplateVersion` carries the spec; the `TemplateDefinition` is
 *     the family it belongs to.
 */

import { z } from 'zod';
import { CreativeFormatSchema } from './catalog.js';

// ---------------------------------------------------------------------------
// Layer types
// ---------------------------------------------------------------------------

export const TEMPLATE_LAYER_TYPES = [
  'TEXT',
  'IMAGE',
  'GENERATED_VISUAL',
  'VIDEO',
  'SHAPE',
  'GRADIENT',
  'QR_CODE',
  'LOGO',
  'BADGE',
  'GROUP',
] as const;
export const TemplateLayerTypeSchema = z.enum(TEMPLATE_LAYER_TYPES);
export type TemplateLayerType = z.infer<typeof TemplateLayerTypeSchema>;

// ---------------------------------------------------------------------------
// Binding whitelist (spec §31)
// ---------------------------------------------------------------------------

export const TEMPLATE_BINDINGS = [
  'tenant.name',
  'tenant.logo.primary',
  'auction.name',
  'auction.logo',
  'auction.publicUrl',
  'auction.biddingOpensAt',
  'auction.biddingClosesAt',
  'auction.marketingDateRange',
  'auction.location',
  'auction.lotCount',
  'lot.hipNumber',
  'lot.name',
  'lot.primaryImage',
  'lot.video',
  // Sale results (ADR-56 hardening D): frozen settlement facts, never a guess.
  'lot.result.status',
  'lot.result.price',
  'auction.results.summary',
  'auction.results.gross',
  'campaign.headline',
  'campaign.cta',
  'campaign.shortUrl',
] as const;
export const TemplateBindingSchema = z.enum(TEMPLATE_BINDINGS);
export type TemplateBinding = z.infer<typeof TemplateBindingSchema>;

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** Insets in pixels from each canvas edge. */
export const SafeAreaSchema = z
  .object({
    top: z.number().nonnegative(),
    right: z.number().nonnegative(),
    bottom: z.number().nonnegative(),
    left: z.number().nonnegative(),
  })
  .strict();
export type SafeArea = z.infer<typeof SafeAreaSchema>;

/** A box in 0..1 fractions of the canvas. Used for layer placement and for
 *  AI quiet zones so the same spec works at any format size. */
export const FractionBoxSchema = z
  .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0).max(1),
    height: z.number().min(0).max(1),
  })
  .strict();
export type FractionBox = z.infer<typeof FractionBoxSchema>;

// ---------------------------------------------------------------------------
// Text layer rules (spec §29-30)
// ---------------------------------------------------------------------------

export const TEXT_ROLES = ['headline', 'subhead', 'date', 'cta', 'cta_url', 'body', 'disclaimer'] as const;
export const TextRoleSchema = z.enum(TEXT_ROLES);
export type TextRole = z.infer<typeof TextRoleSchema>;

export const TEXT_ALIGNMENTS = ['left', 'center', 'right'] as const;
export const TEXT_TRANSFORMS = ['none', 'uppercase', 'lowercase', 'capitalize'] as const;

/** Everything the auto-fit renderer (spec §30) needs to lay a string that
 *  varies in length ("Fall Round Up" vs a long charity-sale name) without ever
 *  silently clipping. Reaching `minFontSize` at `maxLines` without fitting is a
 *  visible validation error, not a crop. */
export const TextLayerRulesSchema = z
  .object({
    role: TextRoleSchema,
    fontFamily: z.string().optional(),
    fontWeight: z.number().int().min(100).max(900).optional(),
    fontSize: z.number().positive(),
    minFontSize: z.number().positive().optional(),
    maxFontSize: z.number().positive().optional(),
    lineHeight: z.number().positive().optional(),
    letterSpacing: z.number().optional(),
    transform: z.enum(TEXT_TRANSFORMS).optional(),
    align: z.enum(TEXT_ALIGNMENTS).optional(),
    maxLines: z.number().int().positive().optional(),
    autoFit: z.boolean().optional(),
    /** WCAG-style minimum contrast the rendered text must meet against its
     *  backing; the renderer validates and fails loudly if unmet. */
    minContrast: z.number().min(1).optional(),
  })
  .strict();
export type TextLayerRules = z.infer<typeof TextLayerRulesSchema>;

// ---------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------

const LayerBase = {
  id: z.string().min(1),
  /** 0..1 placement box within the canvas. */
  box: FractionBoxSchema.optional(),
  /** A locked layer is deterministic HipNumber truth; AI and regeneration must
   *  not move or alter it (spec §16). */
  locked: z.boolean().optional(),
};

export const TemplateLayerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('TEXT'), ...LayerBase, source: TemplateBindingSchema.optional(), text: TextLayerRulesSchema }).strict(),
  z.object({ type: z.literal('IMAGE'), ...LayerBase, source: TemplateBindingSchema.optional() }).strict(),
  z.object({ type: z.literal('LOGO'), ...LayerBase, source: TemplateBindingSchema }).strict(),
  z.object({ type: z.literal('QR_CODE'), ...LayerBase, source: TemplateBindingSchema.optional() }).strict(),
  z.object({ type: z.literal('VIDEO'), ...LayerBase, source: TemplateBindingSchema.optional() }).strict(),
  /** A badge draws `label` and, when `source` is bound, the bound value after
   *  it ("HIP" + lot.hipNumber -> HIP 132): the brand's hip-tag language. */
  z.object({ type: z.literal('BADGE'), ...LayerBase, label: z.string().optional(), source: TemplateBindingSchema.optional() }).strict(),
  z.object({ type: z.literal('SHAPE'), ...LayerBase, fill: z.string().optional() }).strict(),
  z.object({ type: z.literal('GRADIENT'), ...LayerBase, from: z.string().optional(), to: z.string().optional() }).strict(),
  z.object({ type: z.literal('GROUP'), ...LayerBase, children: z.array(z.string().min(1)) }).strict(),
  z
    .object({
      type: z.literal('GENERATED_VISUAL'),
      ...LayerBase,
      role: z.string().optional(),
      /** Where AI should keep the subject, and the zones it must keep visually
       *  quiet for deterministic text/CTA (spec §32). */
      subjectPreference: z.enum(['LEFT', 'RIGHT', 'CENTER', 'TOP', 'BOTTOM']).optional(),
      quietZones: z.array(FractionBoxSchema).optional(),
    })
    .strict(),
]);
export type TemplateLayer = z.infer<typeof TemplateLayerSchema>;

// ---------------------------------------------------------------------------
// Template definition + version
// ---------------------------------------------------------------------------

export const TEMPLATE_KINDS = ['STATIC', 'VIDEO'] as const;
export const TemplateKindSchema = z.enum(TEMPLATE_KINDS);
export type TemplateKind = z.infer<typeof TemplateKindSchema>;

/** One format's layout within a template. A template family carries one of
 *  these per format it supports; each reflows independently (spec §26). */
export const TemplateFormatSpecSchema = z
  .object({
    format: CreativeFormatSchema,
    canvas: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }).strict(),
    safeArea: SafeAreaSchema,
    layers: z.array(TemplateLayerSchema),
  })
  .strict();
export type TemplateFormatSpec = z.infer<typeof TemplateFormatSpecSchema>;

/**
 * An immutable published version of a template. `version` is monotonic within
 * a family; the spec is frozen. A campaign asset records `templateId` +
 * `version`, and this row is never rewritten in place (spec §33).
 */
export const TemplateVersionSchema = z
  .object({
    templateId: z.string().min(1),
    version: z.number().int().positive(),
    kind: TemplateKindSchema,
    /** One layout per format the family supports (the "template family",
     *  spec §26). */
    formats: z.array(TemplateFormatSpecSchema).min(1),
    publishedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type TemplateVersion = z.infer<typeof TemplateVersionSchema>;

/** The family: identity and provenance. Its published versions are separate
 *  immutable rows. `latestVersion` points at the head for the builder UI. */
export const TemplateDefinitionSchema = z
  .object({
    id: z.string().min(1),
    /** Null tenantId = a HipNumber official starter template (spec §79). */
    tenantId: z.string().min(1).nullable(),
    name: z.string().min(1),
    kind: TemplateKindSchema,
    latestVersion: z.number().int().nonnegative(),
    createdAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type TemplateDefinition = z.infer<typeof TemplateDefinitionSchema>;
